import { AfterViewInit, DestroyRef, Directive, ElementRef, inject, input, Renderer2, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractDataContainer } from '@iupics-manager/models/abstract-datacontainer';
import { distinctUntilChanged, of, Subscription, switchMap, tap } from 'rxjs';

@Directive({
  selector: '[iuValidationDisplay]',
  standalone: true,
})
export class ValidationDisplayDirective implements AfterViewInit {
  private readonly renderer = inject(Renderer2);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  enabled = input.required<boolean>({ alias: 'iuValidationDisplay' });
  valid = input.required<undefined | boolean>({ alias: 'iuValidationDisplay.valid' });
  inputSelector = input.required<string>({ alias: 'iuValidationDisplay.inputSelector' });
  iconIdx = input<number>(0, { alias: 'iuValidationDisplay.iconIdx' });
  datacontainer = input<AbstractDataContainer | undefined>(undefined, { alias: 'iuValidationDisplay.datacontainer' });

  private inputEl: HTMLInputElement;
  private iconEl = this.createIconElement();

  private message = signal<string | undefined>(undefined);
  private messageEl = this.createMessageElement();
  private messageTimeout: NodeJS.Timeout;
  private calloutSubscription: Subscription | undefined;

  private readonly enabled$ = toObservable(this.enabled);
  private readonly valid$ = toObservable(this.valid);
  private readonly message$ = toObservable(this.message);

  ngAfterViewInit(): void {
    this.enabled$
      .pipe(
        distinctUntilChanged(),
        switchMap((enabled) => {
          if (!enabled) {
            this.calloutSubscription?.unsubscribe();
            return of();
          }
          // manage message data binding
          const datacontainer = this.datacontainer();
          if (datacontainer && datacontainer.dataStored) {
            this.calloutSubscription?.unsubscribe();
            this.calloutSubscription = datacontainer.dataStored.calloutStackEmptied
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                  const columnName = `Message_IsValid_${datacontainer.data.columnName}`;
                  const message = datacontainer.dataStored.data[columnName];
                  delete datacontainer.dataStored.data[columnName];
                  if (message !== this.message()) {
                    this.message.set(message);
                  }
                })
              )
              .subscribe();
          }

          return this.valid$.pipe(
            distinctUntilChanged(),
            tap((valid: boolean) => {
              this.inputEl = this.elementRef.nativeElement.querySelector(this.inputSelector());
              if (!this.inputEl) {
                return;
              }
              this.iconEl.style.setProperty('--nb-icons', `${this.iconIdx()}`);
              this.renderer.appendChild(this.inputEl.parentElement, this.iconEl);
              this.reset();
              const value = datacontainer?.dataStored.data[datacontainer?.data.columnName];
              if (value === undefined || value === null || value === '') {
                return;
              }
              switch (valid) {
                case true:
                  // set to valid css
                  this.inputEl.style.setProperty('--input-bg-color', 'var(--input-valid-bg-color)');
                  this.inputEl.style.setProperty('--input-bg-color-icon', 'var(--input-valid-bg-color)');
                  this.inputEl.style.setProperty('--filled-mandatory-bg-color', 'var(--input-valid-bg-color)');
                  this.inputEl.style.setProperty('--prime-autocomplete-bg-color-input', 'var(--input-valid-bg-color)');
                  this.inputEl.classList.add('input-valid');
                  this.iconEl.classList.add('input-valid-icon', 'fa-circle-check');
                  break;
                case false:
                  // set to invalid css
                  this.inputEl.style.setProperty('--input-bg-color', 'var(--input-invalid-bg-color)');
                  this.inputEl.style.setProperty('--input-bg-color-icon', 'var(--input-invalid-bg-color)');
                  this.inputEl.style.setProperty('--filled-mandatory-bg-color', 'var(--input-invalid-bg-color)');
                  this.inputEl.style.setProperty(
                    '--prime-autocomplete-bg-color-input',
                    'var(--input-invalid-bg-color)'
                  );
                  this.inputEl.classList.add('input-invalid');
                  this.iconEl.classList.add('input-invalid-icon', 'fa-circle-xmark');
                  break;
              }
            }),
            switchMap(() => {
              return this.message$.pipe(
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
                tap((message) => {
                  // manage message display
                  if (message) {
                    this.renderer.appendChild(this.elementRef.nativeElement.parentNode, this.messageEl);
                    this.messageEl.textContent = message;
                    // this.messageTimeout = setTimeout(() => {
                    //   this.renderer.removeChild(this.elementRef.nativeElement.parentNode, this.messageEl);
                    // }, 3 * 1000);
                    this.destroyRef.onDestroy(() => {
                      clearTimeout(this.messageTimeout);
                    });
                  }
                })
              );
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private reset(): void {
    // remove validation display css
    this.inputEl.classList.remove('input-valid', 'input-invalid');
    this.iconEl.classList.remove('input-valid-icon', 'fa-circle-check', 'input-invalid-icon', 'fa-circle-xmark');
    this.inputEl.style.setProperty('--input-bg-color', null);
    this.inputEl.style.setProperty('--input-bg-color-icon', null);
    this.inputEl.style.setProperty('--filled-mandatory-bg-color', null);
    this.inputEl.style.setProperty('--prime-autocomplete-bg-color-input', null);
  }

  private createIconElement(): HTMLElement {
    const iconEl: HTMLElement = this.renderer.createElement('i');
    iconEl.classList.add('fas', 'float-icon', 'float-icon-right');
    iconEl.setAttribute('aria-hidden', 'true');
    return iconEl;
  }

  private createMessageElement(): HTMLParagraphElement {
    const messageEl: HTMLParagraphElement = this.renderer.createElement('p');
    messageEl.style.marginInlineStart = '.5em';
    messageEl.style.color = 'var(--success-ft-color)';
    messageEl.setAttribute('aria-hidden', 'true');
    return messageEl;
  }
}
